import { Shape, Vector3 } from 'three';
import { generateUUID } from 'three/src/math/MathUtils';
import {
  buildActiveMap,
  createExtrudeSettings,
  createRectangleShape,
  getLengthWithKey,
  getWidthMonument,
  SIDES_INFORMATIONS,
} from './common.utils';

export const getBoxPositionWithIndex = (
  key: 'front' | 'back' | 'left' | 'right',
  depth: { front: number; back: number; left: number; right: number },
  pieces: any[],
  customPlatings: any[],
  customBoxesIsActive: { [key: string]: boolean },
  length: number,
  width: number,
  deltaCenterZ: number,
  mode: 'closoir' | 'double closoir' | 'tete',
) => {
  const box = getWidthMonument({ pieces });
  const deltaX = box.backRight.x - box.backLeft.x - width;
  const deltaZ = mode === 'closoir' ? depth.back / 2 : 0;

  if (box) {
    if (key === 'front') {
      let x = box.frontLeft.x + width / 2 + deltaX / 2;
      let y = box.frontLeft.y;
      let z = box.backLeft.z + length - depth.front - deltaCenterZ;
      return new Vector3(x, y, z);
    }
    if (key === 'back') {
      let x = box.backLeft.x + width / 2 + deltaX / 2;
      let y = box.backLeft.y;
      let z = box.backLeft.z - deltaCenterZ;

      return new Vector3(x, y, z);
    }
    if (key === 'left') {
      let x = box.backLeft.x + deltaX / 2;
      let y = box.frontLeft.y;
      let z = box.backLeft.z + length / 2 - deltaZ - deltaCenterZ;

      return new Vector3(x, y, z);
    }
    if (key === 'right') {
      let x = box.backLeft.x + width - depth.right + deltaX / 2;
      let y = box.frontRight.y;
      let z = box.backRight.z + length / 2 - deltaZ - deltaCenterZ;

      return new Vector3(x, y, z);
    }
  }
};

export const createBoxes = (
  pieces: any[],
  customPlatings: any[],
  customBoxes: any[],
  setCustomBoxes: (customBoxes: any[]) => void,
  customSize?: {
    length?: number;
    width?: number;
    heightFront?: number;
    heightBack?: number;
    orientation?: 'x' | 'z';
    hasSlope?: boolean;
    mode?: 'closoir' | 'double closoir' | 'tete';
    depth?: number;
  },
) => {
  const orientation = customSize?.orientation ?? 'z';
  const mode = customSize?.mode ?? 'closoir';
  const depth = customSize?.depth ?? 0.03;

  let boxes: any[] = [];

  const monumentBox = getWidthMonument({
    pieces,
  });

  const pieceAboveLength = monumentBox.frontRight.z - monumentBox.backRight.z;
  const pieceAboveWidth = monumentBox.frontRight.x - monumentBox.frontLeft.x;

  let boxWidth = pieceAboveWidth > 0 ? pieceAboveWidth - 0.05 : 1.4;
  let boxLength = pieceAboveLength > 0 ? pieceAboveLength - 0.05 : 2.4;

  if (customSize) {
    if (customSize.length) boxLength = customSize.length;
    if (customSize.width) boxWidth = customSize.width;
  }

  const deltaCenterZ = -(pieceAboveLength - boxLength) / 2;

  const customBoxesIsActive = buildActiveMap(customBoxes);

  SIDES_INFORMATIONS.forEach((el, elIndex) => {
    const shape = new Shape();
    const length = getLengthWithKey(
      mode,
      el.key as 'front' | 'back' | 'left' | 'right',
      {
        front: depth,
        back: depth,
        left: depth,
        right: depth,
      },
      el.key === 'front' || el.key === 'back' ? boxWidth : boxLength,
    );

    let shapeDetails = {
      display: true,
      isRotated: el.isRotated,
      depth: depth,
      length: length,
      front: customSize?.heightFront,
      back: customSize?.heightBack,
      monumentWidth: boxWidth,
      monumentLength: boxLength,
      monumentFront: customSize?.heightFront ?? 0.1,
      monumentBack: customSize?.heightBack,
      hasSlope: customSize?.hasSlope,
      orientation: orientation,
      mode,
    };

    if (customSize) {
      if (orientation === 'x' && customSize.heightBack !== undefined) {
        if (el.key === 'left') {
          shapeDetails.back = customSize.heightFront ?? 0.1;
        }
        if (el.key === 'right') {
          shapeDetails.front =
            customSize.heightBack === undefined ? 0.1 : customSize.heightBack;
        }
      }
      if (orientation === 'z' && customSize.heightBack !== undefined) {
        if (el.key === 'front') {
          shapeDetails.back = customSize.heightFront ?? 0.1;
        }
        if (el.key === 'back') {
          shapeDetails.front =
            customSize.heightBack === undefined ? 0.1 : customSize.heightBack;
        }
      }
    }

    if (shapeDetails.front === undefined) shapeDetails.front = 0.1;

    createRectangleShape(shape, length, shapeDetails.front, shapeDetails.back);

    boxes.push({
      texture: customBoxes[elIndex]?.texture,
      granit: customBoxes[elIndex]?.granit,
      id: generateUUID(),
      key: el.key,
      shape,
      shapeDetails,
      isActive: customBoxes[elIndex] ? customBoxes[elIndex].isActive : true,
      position: getBoxPositionWithIndex(
        el.key as 'front' | 'back' | 'left' | 'right',
        {
          front: shapeDetails.depth,
          back: shapeDetails.depth,
          left: shapeDetails.depth,
          right: shapeDetails.depth,
        },
        pieces,
        customPlatings,
        customBoxesIsActive,
        boxLength,
        boxWidth,
        deltaCenterZ,
        mode,
      ),
      extrudeSettings: createExtrudeSettings(depth),
    });
  });

  setCustomBoxes(boxes);
};
